import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from '@/types/errors';
import { ToastProvider } from '@/components/ToastHost';
import { PrincipalProvider } from '@/auth/PrincipalContext';
import { App } from '@/App';
import './styles.css';

// TanStack Query defaults — match frontend-design §6 Retry semantics:
// auto-retry GETs only on 503 / network; skip 401, 403, 404, 422, 409.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (failureCount >= 3) return false;
        if (error instanceof ApiError) {
          if (error.status === 503 || error.status === 0) return true;
          return false;
        }
        // Native fetch network failure — synthesized as ApiError above,
        // so this branch is rare. Allow one extra attempt.
        return failureCount < 1;
      },
      retryDelay: (attempt) => Math.min(2_000, 250 * 2 ** attempt),
    },
    mutations: {
      // POST creates and action endpoints never auto-retry (V1 has no
      // idempotency-key surface). PATCH / DELETE retry is handled in
      // the API client layer for transport errors.
      retry: false,
    },
  },
});

const root = createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <PrincipalProvider>
            <App />
          </PrincipalProvider>
        </ToastProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
